import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  email: string;
  is_admin: boolean;
  policies_accepted_at: string | null;
  policies_version: string | null;
  access_blocked?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isAetvUser: boolean;
  policiesAccepted: boolean;
  isHbsUser: boolean;
  isFlavcityUser: boolean;
  isKnfUser: boolean;
  isDemoUser: boolean;
  isSmartypantsUser: boolean;
  isBacchusUser: boolean;
  accountBlocked: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string; requiresVerification?: boolean; accountDisabled?: boolean }>;
  signup: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  verifyOtp: (email: string, otpCode: string) => Promise<{ success: boolean; message: string; accountDisabled?: boolean }>;
  acceptPolicies: () => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// In Vite, environment variables are accessed via import.meta.env
// and need to be prefixed with VITE_ instead of NEXT_PUBLIC_
const API_URL = import.meta.env.VITE_API_URL || '';
const POLICY_VERSION = import.meta.env.VITE_POLICY_VERSION || '1.0';
const POLICY_ACCEPTANCE_DISABLED = import.meta.env.VITE_POLICY_ACCEPTANCE_DISABLED === 'true';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accountBlocked, setAccountBlocked] = useState(false);

  // Helper function to check if user has aetv.com email domain
  const isAetvUser = user?.email?.endsWith('aegm.com') || false;

  // Helper function to check if user has HBS email domain or is a specific HBS-access user
  const HBS_ACCESS_EMAILS = ['lckeely@gmail.com'];
  const isHbsUser = user?.email?.endsWith('mba2026.hbs.edu') || HBS_ACCESS_EMAILS.includes(user?.email || '') || false;

  const FLAVCITY_ACCESS_EMAILS = ['marie@shopflavcity.com'];
  const isFlavcityUser = FLAVCITY_ACCESS_EMAILS.includes(user?.email || '') || false;

  const KNF_ACCESS_EMAILS = ['knf@blue-pill.ai'];
  const isKnfUser = KNF_ACCESS_EMAILS.includes(user?.email || '') || false;

  const DEMO_ACCESS_EMAILS = ['demo@blue-pill.ai'];
  const isDemoUser = DEMO_ACCESS_EMAILS.includes(user?.email || '') || false;

  // Check if user is from Smartypants domain or in specific access list
  const SMARTYPANTS_ALLOWED_DOMAINS = (import.meta.env.VITE_SMARTYPANTS_ALLOWED_DOMAINS || '')
    .split(',')
    .map((d: string) => d.trim().toLowerCase())
    .filter(Boolean);
  const SMARTYPANTS_ACCESS_EMAILS = ['demo@blue-pill.ai'];
  const userDomain = user?.email?.split('@')[1]?.toLowerCase() || '';
  const isSmartypantsUser = (!!userDomain && SMARTYPANTS_ALLOWED_DOMAINS.includes(userDomain)) ||
    SMARTYPANTS_ACCESS_EMAILS.includes(user?.email || '');

  // Check if user is from Bacchus Worldwide domain
  const BACCHUS_ALLOWED_DOMAINS = (import.meta.env.VITE_BACCHUS_ALLOWED_DOMAINS || '')
    .split(',')
    .map((d: string) => d.trim().toLowerCase())
    .filter(Boolean);
  const isBacchusUser = !!userDomain && BACCHUS_ALLOWED_DOMAINS.includes(userDomain);

  // Logout function (defined early so it can be used in checkAuthStatus)
  const logout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      // Clear user from state and storage
      setUser(null);
      setAccountBlocked(false);
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if user is already logged in by verifying cookie with /me endpoint
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Verify authentication by calling /me endpoint
        const response = await fetch(`${API_URL}/me`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });

        if (response.status === 401 || response.status === 403) {
          // Cookie is invalid or expired, clear user and redirect to login
          setUser(null);
          localStorage.removeItem('user');
          // The App component will handle redirecting to login based on isAuthenticated
        } else if (response.ok) {
          const data = await response.json();
          const userData: User = {
            id: data.id,
            email: data.email,
            is_admin: data.is_admin || false,
            policies_accepted_at: data.policies_accepted_at || null,
            policies_version: data.policies_version || null,
            access_blocked: data.access_blocked || false
          };
          setUser(userData);
          setAccountBlocked(!!data.access_blocked);
          localStorage.setItem('user', JSON.stringify(userData));
        } else {
          // Unexpected error, clear user to be safe
          setUser(null);
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        // On error, clear user to be safe
        setUser(null);
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        // Check if verification is required (unverified user trying to login)
        if (data.requires_verification) {
          return { success: false, message: data.message || 'Email verification required', requiresVerification: true };
        }
        
        const userData: User = {
          id: data.user.id,
          email: email,
          is_admin: data.user.is_admin,
          policies_accepted_at: data.user.policies_accepted_at || null,
          policies_version: data.user.policies_version || null
        };
        setUser(userData);
        setAccountBlocked(false);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, message: 'Login successful' };
      } else if (response.status === 403 && data.detail?.code === 'ACCOUNT_DISABLED') {
        // Free-tier access is disabled — show the "book a call" wall
        setAccountBlocked(true);
        return { success: false, message: data.detail.message || 'Account disabled', accountDisabled: true };
      } else if (response.status === 403 && data.action === 'verify_email') {
        // Handle OTP verification case (legacy)
        return { success: false, message: 'verification_required', requiresVerification: true };
      } else {
        return { success: false, message: data.error || data.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An error occurred during login' };
    }
  };

  // Signup function
  const signup = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        const userData: User = {
          id: data.user?.id || data.user_id,
          email: email,
          is_admin: data.user?.is_admin || false,
          policies_accepted_at: data.user?.policies_accepted_at || null,
          policies_version: data.user?.policies_version || null
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, message: 'Registration successful' };
      } else {
        return { success: false, message: data.error || data.detail || 'Registration failed' };
      }
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, message: 'An error occurred during registration' };
    }
  };

  // OTP verification function
  const verifyOtp = async (email: string, otpCode: string) => {
    try {
      const response = await fetch(`${API_URL}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp_code: otpCode }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const userData: User = {
          id: data.user.id,
          email: data.user.email,
          is_admin: data.user.is_admin,
          policies_accepted_at: data.user.policies_accepted_at || null,
          policies_version: data.user.policies_version || null
        };
        setUser(userData);
        setAccountBlocked(false);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, message: 'Verification successful' };
      } else if (response.status === 403 && data.detail?.code === 'ACCOUNT_DISABLED') {
        // Free-tier access is disabled — show the "book a call" wall
        setAccountBlocked(true);
        return { success: false, message: data.detail.message || 'Account disabled', accountDisabled: true };
      } else {
        return { success: false, message: data.error || data.detail || data.message || 'Verification failed' };
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      return { success: false, message: 'An error occurred during verification' };
    }
  };

  const acceptPolicies = async () => {
    try {
      const response = await fetch(`${API_URL}/accept-policies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: POLICY_VERSION }),
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (user) {
          const updatedUser: User = {
            ...user,
            policies_accepted_at: data.policies_accepted_at,
            policies_version: POLICY_VERSION
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        return { success: true, message: 'Policies accepted' };
      }
      return { success: false, message: data.detail || 'Failed to accept policies' };
    } catch (error) {
      console.error('Accept policies error:', error);
      return { success: false, message: 'An error occurred' };
    }
  };

  const policiesAccepted = POLICY_ACCEPTANCE_DISABLED || (!!user?.policies_accepted_at && user?.policies_version === POLICY_VERSION);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || false,
        isAetvUser,
        policiesAccepted,
        isHbsUser,
        isFlavcityUser,
        isKnfUser,
        isDemoUser,
        isSmartypantsUser,
        isBacchusUser,
        accountBlocked,
        login,
        signup,
        verifyOtp,
        acceptPolicies,
        logout
      }}
    >
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};